import tools from '@/sui/data/sheets/edit/tools'
import fields from '@/sui/data/sheets/edit/fields'

import go from '@/gojs/go'

function buildAccTest (v1, v2) {
  return {
    v1: tools.inList(fields.accessSite, v1),
    v2: tools.inList(fields.accessSite, v2)
  }
}

const select = {
  kt: buildAccTest([3], ['D1']),
  acc: buildAccTest([1, 2, 3], ['D1', 'D2', 'D3']),
  fnat: buildAccTest([1], ['D2']),
  fist: buildAccTest([2, 3], ['D1', 'D3']),
  inf: tools.equals2(fields.infection, 1, 2),
  abl: tools.equals(fields.ablated, 1),
  usee: tools.inList(fields.useEnded, [1, 2, 3])
}

const firstSubstn = tools.cfg.date(
  fields.firstSubstn, 'de première suppléance', {
    op: true,
    tt: `Noter la date de premier traitement de l’Insuffisance Rénale
Terminale (IRT) par dialyse péritonéale, hémodialyse ou transplantation
(jj/mm/aaaa) `
  }
)

const catheterFirstUse = (function () {
  function build (v) {
    return tools.cfg.date(
      fields.firstUse, 'de première utilisation', {
        op: true,
        sel: select.acc[v]
      }
    )
  }
  return { v1: build('v1'), v2: build('v2') }
})()

const catheterAblation = (function () {
  function build (v) {
    return tools.cfg.date(
      fields.ablation, `d'ablation`, {
        sel: tools.allTrue(select.fist[v], select.abl)
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const catheterUseEnding = (function () {
  function build (v) {
    return tools.cfg.date(
      fields.useEnding, `d'arrêt d'utilisation`, {
        sel: tools.allTrue(select.fnat[v], select.usee)
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const infectionDate = tools.cfg.date(
  fields.date, `de l'infection`,
  { sel: select.inf }
)

const entryDate = tools.cfg.date(
  fields.entrydate, `d'entrée dans la surveillance`
)

const lock = (function () {
  function buildCap (prefix, v) {
    return tools.cfg.list(
      `${prefix}cap`, `Bouchon de fermeture à membrane`,
      go.fields.yesNoUnknown, {
        sel: select.kt[v]
      }
    )
  }

  function buildV1 (prefix) {
    const locks = [
      { field: fields.heparine, name: 'héparine' },
      { field: fields.citrate, name: 'citrate' },
      { field: fields.antibio, name: 'antibiotique' },
      { field: fields.serumPhy, name: 'sérum physiologique' },
      { field: fields.taurine, name: 'taurine' },
      { field: fields.alcohol, name: 'alcool' },
      { field: fields.other, name: 'autre' }
    ]
    const result = []
    for (let i in locks) {
      const lock = locks[i]
      result.push(
        tools.cfg.list(
          `${prefix}${lock.field}`, `Verrou ${lock.name}`, [
            { code: 1, value: `1. Verrou ${lock.name}` },
            { code: 2, value: `2. Pas de verrou ${lock.name}` },
            { code: 9, value: `9. Non connu` }
          ], { sel: select.kt.v1 }
        )
      )
    }

    result.push(buildCap(prefix, 'v1'))

    return result
  }

  function buildV2 (prefix, title) {
    return [
      tools.cfg.list(
        `${prefix}Lock`, `Verrou ${title}`, go.fields.diaLock, {
          sel: select.kt.v2
        }
      ),
      buildCap(prefix, 'v2')
    ]
  }

  return {
    install: {
      v1: buildV1('install'),
      v2: buildV2('install', 'lors de la pose')
    },
    disconnect: {
      v1: buildV1('disconnect'),
      v2: buildV2('disconnect', 'débranchement')
    }
  }
})()

const hemodiafiltr = tools.cfg.list(
  fields.hemodiafiltr, 'Hémodiafiltration on line', go.fields.yesNoUnknown
)

const treatedDiab = tools.cfg.list(
  fields.treatedDiab, 'Diabète traité', go.fields.yesNoUnknown
)

const immuno = tools.cfg.list(
  fields.immuno, 'Immunodépression', go.fields.yesNoUnknown
)

const hygiene = tools.cfg.list(
  fields.hygiene, `Niveau d'hygiène corporelle`, go.fields.hygiene
)

const previousInfect = tools.cfg.list(
  fields.previousInfect, `Antécédent d'infection à <i>S. AUREUS</i>`,
  go.fields.yesNoUnknown
)

const previousBact = tools.cfg.list(
  fields.previousBact, `Antécédent de bactériémie`, go.fields.yesNoUnknown
)

const toxico = tools.cfg.list(
  fields.toxico, `Toxicomanie active`, go.fields.yesNoUnknown
)

const vih = tools.cfg.list(
  fields.vih, `Sérologie VIH positive`, go.fields.yesNoUnknown
)

const vhc = tools.cfg.list(
  fields.vhc, `Sérologie VHC positive`, go.fields.yesNoUnknown
)

const height = tools.cfg.numeric(
  fields.height, 'Taille', 3, {
    un: 'cm', tt: 'Entrer la taille en cm.', op: true
  }
)

const weight = tools.cfg.numeric(
  fields.weight, 'Poids sec', 3, {
    un: 'kg', tt: 'Entrer le poids sec en kg.'
  }
)

const ferritinaemia = tools.cfg.numeric(
  fields.ferritinaemia, 'Ferritinemie', 4, {
    un: 'µg/L',
    tt: 'Entrer la ferritinemie en µg/L.',
    op: true
  }
)

const saturation = tools.cfg.numeric(
  fields.saturation, 'Coefficient de saturation', 3, {
    un: '%',
    tt: 'Entrer le coefficient de saturation.',
    op: true
  }
)

const hemoglobin = tools.cfg.numeric(
  fields.hemoglobin, `Taux d'hémoglobine`, 3, {
    un: 'g/dL',
    tt: `Entrer le taux d'hémoglobine en g/dL.`,
    op: true
  }
)

const albumin = tools.cfg.numeric(
  fields.albumin, 'Albuminémie', 2, {
    un: 'g/L',
    tt: `Entrer l'albuminémie en g/L.`,
    op: true
  }
)

const creatinine = tools.cfg.numeric(
  fields.creatinine, 'Créatininémie pré-dialyse', 4, {
    un: 'µmol/L',
    tt: `Entrer la créatininémie en µmol/L.`,
    op: true
  }
)

const dialNumber = tools.cfg.numeric(
  fields.dialNumber, 'Nombre de séances de dialyse', 3, {
    tt: `Entrer le nombre de séances de dialyse pendant la période
de suivi.`
  }
)

const cardioVascPb = tools.cfg.list(
  fields.cardioVascPb, 'Problèmes cardio-vasculaires', go.fields.yesNoUnknown
)

const surgery = tools.cfg.list(
  fields.surgery, 'Acte chirurgical', go.fields.yesNoUnknown
)

const transfusions = tools.cfg.numeric(
  fields.transfusions, 'Nombre total de transfusions', 2, {
    tt: `Entrer le nombre total de transfusions pendant la période de
suivi`
  }
)

const exit = tools.cfg.date(fields.exit, 'de fin du suivi')

const exitReason = tools.cfg.list(
  fields.exitReason, 'Motif de fin de suivi', go.fields.diaExitReason
)

const accessSite = (function () {
  function build (f, v) {
    return tools.cfg.list(
      fields.accessSite, `Site d'accès`, f,
      { pop: tools.not(select.acc[v]) }
    )
  }

  return {
    v1: build(go.fields2019.diaAccessSite, 'v1'),
    v2: build(go.fields.diaAccessSite, 'v2')
  }
})()

const uses = (function () {
  function build (v) {
    return tools.cfg.numeric(
      fields.uses, `Nombre d'utilisations (période suivi)`, 4, {
        tt: `Entrer le nombre d'utilisations du site d'accès pendant
la période de suivi.`,
        sel: select.acc[v]
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const incidents = (function () {
  function build (v) {
    return tools.cfg.numeric(
      fields.incidents,
      `Nombre de séances avec incidents / manipulations (période suivi)`,
      4, {
        tt: `Entrer le nombre de séances avec incidents ou
manipulations du site d'accès pendant la période de suivi.`,
        sel: select.acc[v]
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const unrelatedManip = (function () {
  function build (v) {
    return tools.cfg.numeric(
      fields.unrelatedManip,
      `Nombre de manipulations sans rapport avec séance (période suivi)`,
      4, {
        tt: `Entrer le nombre de manipulations du site sans rapport
avec une séance pendant la période de suivi.`,
        sel: select.acc[v]
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const ablated = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.ablated, `Ablation`, go.fields.yesNoUnknown, {
        sel: select.fist[v]
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const useEnded = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.useEnded, `Arrêt d'utilisation`, go.fields.diaUseEnded, {
        sel: select.fnat[v]
      }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const installReason = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.installReason, 'Motif de pose du cathéter',
      go.fields.diaInstallReason, { sel: select.kt[v] }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const tunneled = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.tunneled, 'Cathéter tunnelisé', go.fields.yesNoUnknown,
      { sel: select.kt[v] }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const installSite = (function () {
  function build (v, f) {
    return tools.cfg.list(
      fields.installSite, 'Site de pose', f,
      { sel: select.kt[v] }
    )
  }

  return {
    v1: build('v1', go.fields2019.diaInstallSite),
    v2: build('v2', go.fields.diaInstallSite)
  }
})()

const siteChanges = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.siteChanges, 'Changement de site', go.fields.yesNoUnknown,
      { sel: select.kt[v] }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const antibiopr = (function () {
  function build (v) {
    return tools.cfg.list(
      fields.antibiopr, 'Antibioprophylaxie per op', go.fields.yesNoUnknown,
      { sel: select.kt[v] }
    )
  }

  return { v1: build('v1'), v2: build('v2') }
})()

const antibio = tools.cfg.list(
  fields.antibio, 'Traitement antibiotique par voie générale',
  go.fields.yesNoUnknown, { sel: select.inf }
)

const infectionField = tools.cfg.list(
  fields.infection, 'Infection liée au site',
  go.fields.diaInfection, {
    pop: tools.not(select.inf)
  }
)

export default {
  select,
  catheterFirstUse,
  firstSubstn,
  catheterAblation,
  catheterUseEnding,
  infectionDate,
  entryDate,

  lock,
  hemodiafiltr,
  treatedDiab,
  immuno,
  hygiene,
  previousInfect,
  previousBact,
  toxico,
  vih,
  vhc,
  height,
  weight,
  ferritinaemia,
  saturation,
  hemoglobin,
  albumin,
  creatinine,
  dialNumber,
  cardioVascPb,
  surgery,
  transfusions,
  exit,
  exitReason,
  accessSite,
  uses,
  incidents,
  unrelatedManip,
  ablated,
  useEnded,
  installReason,
  tunneled,
  installSite,
  siteChanges,
  antibiopr,
  antibio,
  infectionField
}
